import {
  CaseClinicalSpecialistResource,
  CaseOCSConsoleResource,
  CaseResource,
  CaseResourceType,
  CaseRetrievalSurgeonResource,
} from '@/api/apis/case';
import {
  AccommodationLeg,
  AircraftLegResource,
  AircraftLogisticsResourceType,
  AirportLegLocationReference,
  AviaLegTypes,
  BaseLegResourceType,
  CrewLogisticsResourceType,
  CustomAddressLegLocationReference,
  FacilityLegLocationReference,
  GroundLegTypes,
  LogisticsLegResourceType,
  RouteLocationType,
  SegmentTypeEnum,
} from '@/api/apis/route';
import { RoutePlanMetrics, TeamResourcesByKeyword } from '@/api/apis/mission-planner';
import { Employee } from '@/api/apis/resources';

export enum RouteFilterType {
  Full = 'full',
  CS = 'cs',
  Surgeon = 'surgeon',
}

export type RouteResourcesType = {
  [CaseResourceType.ClinicalSpecialist]: CaseResource<CaseClinicalSpecialistResource>[];
  [CaseResourceType.OCSConsole]: CaseResource<CaseOCSConsoleResource>[];
  [CaseResourceType.Surgeon]: CaseResource<CaseRetrievalSurgeonResource>[];
  [CaseResourceType.Aircraft]: AircraftLogisticsResourceType[];
  [CaseResourceType.Crew]: CrewLogisticsResourceType[];
};

export type RouteContextResourcesType = {
  [CaseResourceType.ClinicalSpecialist]: Pick<
    CaseResource<CaseClinicalSpecialistResource>,
    'resourceType' | 'resourceId' | 'data'
  >[];
  [CaseResourceType.OCSConsole]: Pick<CaseResource<CaseOCSConsoleResource>, 'resourceType' | 'resourceId' | 'data'>[];
  [CaseResourceType.Surgeon]: Pick<
    CaseResource<CaseRetrievalSurgeonResource>,
    'resourceType' | 'resourceId' | 'data'
  >[];
  [CaseResourceType.Aircraft]: AircraftLogisticsResourceType[];
  [CaseResourceType.Crew]: CrewLogisticsResourceType[];
};

export type LogisticsResourceWithFallback = LogisticsLegResourceType & {
  fallbackInfo: Pick<Employee, 'id' | 'firstName' | 'lastName' | 'profilePhoto'> | null;
};

export type RoutePlanResources = {
  [CaseResourceType.ClinicalSpecialist]: LogisticsResourceWithFallback;
  [CaseResourceType.OCSConsole]: LogisticsResourceWithFallback;
  [CaseResourceType.Surgeon]: LogisticsResourceWithFallback;
  [CaseResourceType.Aircraft]: BaseLegResourceType<AircraftLegResource>[];
};

export type RouteSegmentType = {
  id: string;
  startLocation: AirportLegLocationReference | CustomAddressLegLocationReference | FacilityLegLocationReference;
  type: SegmentTypeEnum;
  isAccommodation?: boolean;
  repositionType?: SegmentTypeEnum;
  endTime?: string | null;
  startTime?: string | null;
  actualStartTime?: string | null;
  actualEndTime?: string | null;
  endLocation: AirportLegLocationReference | CustomAddressLegLocationReference | FacilityLegLocationReference;
};

export type RoutePlanType = {
  id: string;
  segments: RouteSegmentType[];
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  accommodationLegs: Map<string, AccommodationLeg[]>;
  metrics: RoutePlanMetrics;
  dryRunSegments: RouteSegmentType[];
  dryRunLegs: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  dryRunAccommodationLegs: Map<string, AccommodationLeg[]>;
  dryRunMetrics: RoutePlanMetrics;
  resources: Partial<RoutePlanResources>;
  teamResources: Partial<TeamResourcesByKeyword>;
  locations: RouteLocationType[];
  isValid: boolean;
  hasFakeCommercialFlights: boolean;
  orTimeDiffMs?: number;
};
